import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiService from '../../services/api';
import Swal from 'sweetalert2';

const Container = styled.div`
  padding: 24px;
  height: calc(100vh - 64px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const Title = styled.h1`
  color: #2d3748;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 15px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 12px;
  font-weight: 600;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 10;

  &:nth-child(1) { width: 60px; text-align: center; }
  &:nth-child(2) { width: 120px; text-align: center; }
  &:nth-child(3) { width: 250px; }
  &:nth-child(4) { width: 180px; text-align: center; }
  &:nth-child(5) { width: 120px; text-align: center; }
  &:nth-child(6) { width: 200px; text-align: center; }
`;

const Td = styled.td`
  padding: 14px 12px;
  border-bottom: 1px solid #e2e8f0;
  color: #4a5568;

  &.brand-code {
    font-weight: 700;
    font-family: 'Courier New', Courier, monospace;
    color: #667eea;
    text-align: center;
  }

  &.brand-name {
    font-weight: 600;
    color: #2d3748;
  }

  &.sender-id {
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    color: #718096;
    text-align: center;
  }

  &.status-cell {
    text-align: center;
  }

  &.actions-cell {
    text-align: center;
    white-space: nowrap;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;

  ${props => {
    if (props.status === 'active') {
      return `
        background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
        color: white;
      `;
    } else if (props.status === 'inactive') {
      return `
        background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
        color: white;
      `;
    } else if (props.status === 'suspended') {
      return `
        background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
        color: white;
      `;
    } else {
      return `
        background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
        color: white;
      `;
    }
  }}
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin: 0 4px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  min-width: 65px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }

  ${props => {
    if (props.variant === 'edit') {
      return `
        background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
        color: white;
        &:hover { box-shadow: 0 4px 8px rgba(66, 153, 225, 0.3); }
      `;
    } else if (props.variant === 'activate') {
      return `
        background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
        color: white;
        &:hover { box-shadow: 0 4px 8px rgba(72, 187, 120, 0.3); }
      `;
    } else if (props.variant === 'deactivate') {
      return `
        background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
        color: white;
        &:hover { box-shadow: 0 4px 8px rgba(237, 137, 54, 0.3); }
      `;
    } else if (props.variant === 'delete') {
      return `
        background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
        color: white;
        &:hover { box-shadow: 0 4px 8px rgba(245, 101, 101, 0.3); }
      `;
    }
  }}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;
  font-size: 18px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #e53e3e;
  background: linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%);
  border-radius: 12px;
  margin: 20px 0;
  font-weight: 600;
  font-size: 16px;

  button {
    margin-top: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 20px;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
    }
  }
`;

const BrandsList = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getBrands();
      if (response.data && response.data.data) {
        setBrands(response.data.data);
      } else {
        setBrands([]);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBrand = () => {
    navigate('/admin-dhobicart/brands/register');
  };

  const handleEditBrand = (brandId) => {
    navigate(`/admin-dhobicart/brands/edit/${brandId}`);
  };

  const handleToggleStatus = async (brand) => {
    try {
      const newStatus = brand.status === 'active' ? 'inactive' : 'active';

      const result = await Swal.fire({
        title: 'Change Status',
        text: `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} ${brand.brandCode}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#667eea',
        cancelButtonColor: '#718096',
        confirmButtonText: `Yes, ${newStatus === 'active' ? 'Activate' : 'Deactivate'}`,
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        await apiService.updateBrandStatus(brand._id, newStatus);

        Swal.fire({
          title: 'Success!',
          text: `Brand ${brand.brandCode} has been ${newStatus === 'active' ? 'activated' : 'deactivated'}`,
          icon: 'success',
          confirmButtonColor: '#667eea',
          timer: 2000
        });

        fetchBrands();
      }
    } catch (error) {
      console.error('Error updating brand status:', error);

      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to update brand status',
        icon: 'error',
        confirmButtonColor: '#e53e3e'
      });
    }
  };

  const handleDeleteBrand = async (brand) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Brand',
        text: `Are you sure you want to permanently delete "${brand.brandCode}"? This action cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e53e3e',
        cancelButtonColor: '#718096',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        input: 'text',
        inputPlaceholder: `Type "${brand.brandCode}" to confirm`,
        inputValidator: (value) => {
          if (value !== brand.brandCode) {
            return 'Please type the brand code exactly to confirm deletion';
          }
        }
      });

      if (result.isConfirmed) {
        await apiService.deleteBrand(brand._id);
        Swal.fire({
          title: 'Deleted!',
          text: `Brand ${brand.brandCode} has been permanently deleted`,
          icon: 'success',
          confirmButtonColor: '#667eea',
          timer: 1800
        });
        fetchBrands();
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to delete brand',
        icon: 'error',
        confirmButtonColor: '#e53e3e'
      });
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading brands...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          Error: {error}
          <br />
          <button onClick={fetchBrands}>Retry</button>
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Registered Brands</Title>
        <AddButton onClick={handleAddBrand}>+ Register New Brand</AddButton>
      </Header>

      { brands.length === 0 ? (
        <LoadingMessage>No brands found. Click "Register New Brand" to add one.</LoadingMessage>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>S.No</Th>
                <Th>Brand Code</Th>
                <Th>Brand Name</Th>
                <Th>Sender ID</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand, index) => (
                <tr key={brand._id}>
                  <Td style={{ textAlign: 'center', fontWeight: '600', color: '#718096' }}>
                    {index + 1}
                  </Td>
                  <Td className="brand-code">{brand.brandCode}</Td>
                  <Td className="brand-name">{brand.brandName}</Td>
                  <Td className="sender-id">{brand.senderId}</Td>
                  <Td className="status-cell">
                    <StatusBadge status={brand.status || 'active'}>
                      {brand.status || 'active'}
                    </StatusBadge>
                  </Td>
                  <Td className="actions-cell">
                    <ActionButton
                      variant="edit"
                      onClick={() => handleEditBrand(brand._id)}
                    >
                      Edit
                    </ActionButton>
                    <ActionButton
                      variant={brand.status === 'active' ? 'deactivate' : 'activate'}
                      onClick={() => handleToggleStatus(brand)}
                    >
                      {brand.status === 'active' ? 'Deactivate' : 'Activate'}
                    </ActionButton>
                    <ActionButton
                      variant="delete"
                      onClick={() => handleDeleteBrand(brand)}
                    >
                      Delete
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default BrandsList;
